/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印入口照片规则
 */

export interface BatchEditWatermarkPhotoCandidate {
  /** 照片稳定序号，用于 photoPicker 去重。 */
  seq?: string | number
  /** 服务端照片 ID；可用时优先于页面去重序号。 */
  photoID?: number
  /** 媒体类型：0 为照片，1 为视频；历史入口可能不提供。 */
  mediaType?: number
  /** 照片来源：4 为拼图汇报，当前不支持批量编辑水印。 */
  sourceType?: number
  /** 是否包含水印；字段缺失时沿用历史入口校验。 */
  hasWatermark?: boolean
  /** 基础水印 ID，用于识别当前不支持的水印类型。 */
  watermarkBaseID?: string | number
  /** 团队水印 ID，与基础水印 ID 共同确定可靠的同款水印。 */
  groupWatermarkID?: string | number
  /** 编辑器使用的水印实例 ID，不作为入口同款判断的唯一依据。 */
  watermarkID?: string | number
  /** 原图地址，AI 文件夹入口会校验该字段。 */
  file?: string
  /** 列表缩略图地址，入口校验弹窗优先展示。 */
  smallURL?: string
  /** 大图地址，缩略图缺失时用于入口预览。 */
  largeURL?: string
  /** 批量编辑上下文使用的通用图片地址。 */
  url?: string
  /** 拍摄成员 ID，用于无同款结果时提供同成员降级筛选。 */
  userID?: string | number
  /** 拍摄成员名称，用于入口扩选提示。 */
  userNickname?: string
  /** 拍摄时间戳，用于入口扩选提示。 */
  time?: number
  /** 照片扩展信息，普通照片入口从中读取防伪码。 */
  extension?: string | Record<string, unknown> | null
  /** 照片可编辑，但因无防伪码而不支持验真。 */
  verificationUnavailable?: boolean
}

export type PhotoValidationMode = 'standard' | 'required_fields'
export type PhotoPickerMode = 'append' | 'replace'
export type BatchWatermarkInvalidReason =
  | 'video'
  | 'collage_report'
  | 'no_watermark'
  | 'unsupported_watermark'
  | 'missing_identifier'
  | 'missing_file'

/**
 * 判断入口候选项是否属于批量编辑水印支持的图片类型。
 *
 * 历史入口未提供 mediaType 时继续按图片处理，避免影响文件夹等既有链路。
 *
 * @param photo - 待判断的入口候选项
 * @returns 明确为视频时返回 false，其余情况返回 true
 *
 * @example
 * ```ts
 * isBatchWatermarkImageCandidate({ mediaType: 0 }) // true
 * isBatchWatermarkImageCandidate({ mediaType: 1 }) // false
 * ```
 */
export const isBatchWatermarkImageCandidate = (
  photo: BatchEditWatermarkPhotoCandidate
): boolean => photo.mediaType !== 1

/**
 * 判断照片扩展信息中是否包含可用的防伪码。
 *
 * 防伪码只决定照片本身能否验真，不再参与批量编辑资格判断。
 *
 * @param photo - 待检查的照片
 * @returns 包含非空防伪码时返回 true
 *
 * @example
 * ```ts
 * hasBatchWatermarkAntiFakeCode({
 *   extension: '{"antiFakeCode":"code"}'
 * }) // true
 * ```
 */
export function hasBatchWatermarkAntiFakeCode(
  photo: BatchEditWatermarkPhotoCandidate
): boolean {
  if (!photo.extension) {
    return false
  }

  try {
    const extension: unknown =
      typeof photo.extension === 'string'
        ? JSON.parse(photo.extension)
        : photo.extension
    if (!extension || typeof extension !== 'object') {
      return false
    }

    const antiFakeCode = Reflect.get(extension, 'antiFakeCode')
    return typeof antiFakeCode === 'string' && antiFakeCode.trim().length > 0
  } catch {
    return false
  }
}

/**
 * 根据入口数据能力标记照片的验真可用性。
 *
 * 只有标准团队照片接口会读取 extension。分类文件夹等必要字段模式不返回
 * extension，不能据此推断照片没有防伪码。
 *
 * @param photo - 已通过编辑资格校验的照片
 * @param validationMode - 当前入口的数据校验模式
 * @returns 带有稳定验真可用性标记的照片
 *
 * @example
 * ```ts
 * markBatchWatermarkVerificationAvailability({ seq: 1 }, 'standard')
 * // { seq: 1, verificationUnavailable: true }
 * ```
 */
export function markBatchWatermarkVerificationAvailability<
  Photo extends BatchEditWatermarkPhotoCandidate
>(photo: Photo, validationMode: PhotoValidationMode): Photo {
  if (validationMode !== 'standard') {
    return photo
  }

  return {
    ...photo,
    verificationUnavailable: !hasBatchWatermarkAntiFakeCode(photo)
  }
}

/**
 * 统计当前照片中因无防伪码而不支持验真的数量。
 *
 * @param photos - 已完成入口验真可用性标记的照片
 * @returns 不支持验真的照片数量
 *
 * @example
 * ```ts
 * countBatchWatermarkVerificationUnavailablePhotos([
 *   { verificationUnavailable: true },
 *   { verificationUnavailable: false }
 * ]) // 1
 * ```
 */
export function countBatchWatermarkVerificationUnavailablePhotos(
  photos: readonly BatchEditWatermarkPhotoCandidate[]
): number {
  return photos.filter((photo) => photo.verificationUnavailable === true).length
}

/**
 * 获取入口候选内容不可编辑的首要原因。
 *
 * 规则顺序按用户最容易理解、也最适合恢复的原因排列。字段未提供时保留
 * 历史入口兼容行为，避免文件夹等旧数据源被误判。
 *
 * @param photo - 待校验的照片或视频
 * @param validationMode - 当前入口的校验模式
 * @returns 可编辑时返回 null，否则返回稳定的原因枚举
 */
export const getBatchWatermarkInvalidReason = (
  photo: BatchEditWatermarkPhotoCandidate,
  validationMode: PhotoValidationMode
): BatchWatermarkInvalidReason | null => {
  if (!isBatchWatermarkImageCandidate(photo)) {
    return 'video'
  }

  if (photo.sourceType === 4) {
    return 'collage_report'
  }

  if (photo.seq === undefined || photo.seq === null) {
    return 'missing_identifier'
  }

  if (validationMode === 'required_fields') {
    return photo.file ? null : 'missing_file'
  }

  if (photo.hasWatermark === false) {
    return 'no_watermark'
  }

  if (String(photo.watermarkBaseID ?? '') === '21') {
    return 'unsupported_watermark'
  }

  return null
}

/**
 * 判断照片是否满足当前入口的编辑要求。
 *
 * @param photo - 待校验照片
 * @param validationMode - 标准照片或必要字段校验模式
 * @returns 是否允许进入批量编辑
 *
 * @example
 * ```ts
 * isEditableBatchWatermarkPhoto({ seq: 1, file: 'photo.jpg' }, 'required_fields')
 * ```
 */
export const isEditableBatchWatermarkPhoto = (
  photo: BatchEditWatermarkPhotoCandidate,
  validationMode: PhotoValidationMode
): boolean => getBatchWatermarkInvalidReason(photo, validationMode) === null

/**
 * 获取当前校验模式对应的空结果提示。
 *
 * @param validationMode - 照片校验模式
 * @returns 面向用户的提示文案
 *
 * @example
 * ```ts
 * getBatchWatermarkEmptyPhotoMessage('required_fields')
 * ```
 */
export const getBatchWatermarkEmptyPhotoMessage = (
  validationMode: PhotoValidationMode
): string =>
  validationMode === 'required_fields'
    ? '选中的照片缺少必要字段（seq / file），无法进入批量编辑水印'
    : '选中的内容暂不支持批量编辑水印，请选择符合条件的照片'

/**
 * 获取结果埋点使用的照片校验失败原因。
 *
 * @param validationMode - 照片校验模式
 * @returns 稳定的埋点错误原因
 *
 * @example
 * ```ts
 * getBatchWatermarkValidationError('standard')
 * ```
 */
export const getBatchWatermarkValidationError = (
  validationMode: PhotoValidationMode
): string =>
  validationMode === 'required_fields'
    ? '照片缺少必要字段'
    : '内容不符合批量编辑要求'

/**
 * 按照片 seq 去除入口内重复项和 photoPicker 已存在项。
 *
 * @param photos - 校验通过的照片
 * @param existingSeqs - photoPicker 中已有的照片序号
 * @returns 保留首次出现且尚未写入 photoPicker 的照片
 *
 * @example
 * ```ts
 * deduplicateBatchWatermarkPhotos([{ seq: 1 }, { seq: 1 }], new Set())
 * ```
 */
export const deduplicateBatchWatermarkPhotos = <
  Photo extends BatchEditWatermarkPhotoCandidate
>(
  photos: readonly Photo[],
  existingSeqs: ReadonlySet<string | number>
): Photo[] => {
  const seenSeqs = new Set(existingSeqs)
  return photos.filter((photo) => {
    if (photo.seq === undefined || photo.seq === null) {
      return false
    }
    if (seenSeqs.has(photo.seq)) {
      return false
    }
    seenSeqs.add(photo.seq)
    return true
  })
}
